import { SCORING } from '@/config/audio';
import { bassNote, expectedNotes, expectedPitchClasses, STANDARD_TUNING_MIDI, midiToFrequency, midiToPitchClass } from '@/data/tuning';
import type { Chord, ExpectedNote, PitchClass } from '@/types/music';
import { clamp01, normalizeRange } from '../dsp/stats';

/** Everything the scorer needs about a chord, precomputed once. */
export interface ChordTarget {
  chordId: string;
  name: string;
  notes: ExpectedNote[];
  pitchClasses: PitchClass[];
  /** Membership lookup, index = pitch class. */
  isExpected: Uint8Array;
  root: PitchClass;
  bass: ExpectedNote | null;
  /** Fundamentals of strings the player is supposed to *not* sound. */
  mutedFundamentals: number[];
  /** Per-pitch-class weight used inside the purity term. */
  weights: Float32Array;
}

export function buildTarget(chord: Chord): ChordTarget {
  const notes = expectedNotes(chord.frets);
  const pitchClasses = expectedPitchClasses(chord.frets);
  const bass = bassNote(chord.frets);
  const isExpected = new Uint8Array(12);
  for (const pc of pitchClasses) isExpected[pc] = 1;

  const weights = new Float32Array(12).fill(1);
  weights[chord.root] = SCORING.rootWeight;
  if (bass) {
    weights[bass.pitchClass] = Math.max(weights[bass.pitchClass], SCORING.bassWeight);
  }

  const mutedFundamentals: number[] = [];
  for (let i = 0; i < chord.frets.length; i++) {
    if (chord.frets[i] === null) {
      mutedFundamentals.push(midiToFrequency(STANDARD_TUNING_MIDI[i]));
    }
  }

  return {
    chordId: chord.id,
    name: chord.name,
    notes,
    pitchClasses,
    isExpected,
    root: chord.root,
    bass,
    mutedFundamentals,
    weights,
  };
}

/** Evidence about the bass register, measured from the spectrum by the analyser. */
export interface BassEvidence {
  /** SNR in dB of the expected lowest fundamental over the noise floor. */
  expectedDb: number;
  /**
   * SNR in dB of the loudest *muted* string fundamental that sits below the
   * expected bass. `-Infinity` when the chord mutes nothing below its bass.
   */
  unwantedDb: number;
}

export interface ScoreTerms {
  purity: number;
  coverage: number;
  root: number;
  bass: number;
}

/**
 * Score one chroma frame against a known target chord.
 *
 * The four terms answer four different questions, which is why they are kept
 * separate rather than collapsed into one similarity number:
 *
 * - purity   — is the energy where it should be, or is something else ringing?
 * - coverage — is every note of the chord actually present, or did one die?
 * - root     — is the chord's root audible at all?
 * - bass     — did the player hit a string they were supposed to avoid?
 */
export function scoreTargetChord(
  chroma: Float32Array,
  target: ChordTarget,
  bass?: BassEvidence,
): ScoreTerms {
  let total = 0;
  let inSum = 0;
  let weightedIn = 0;
  let weightedOut = 0;
  let maxChroma = 0;

  for (let pc = 0; pc < 12; pc++) {
    const value = chroma[pc];
    total += value;
    if (value > maxChroma) maxChroma = value;
    if (target.isExpected[pc]) {
      inSum += value;
      weightedIn += value * target.weights[pc];
    } else {
      weightedOut += value;
    }
  }

  if (total <= 0 || maxChroma <= 0) {
    return { purity: 0, coverage: 0, root: 0, bass: 0 };
  }

  // Raw share of energy on expected pitch classes, then stretched across the
  // band that actually separates chords (see SCORING.purityFloor).
  const rawPurity = clamp01(weightedIn / (weightedIn + weightedOut));
  const purity = normalizeRange(rawPurity, SCORING.purityFloor, SCORING.purityCeiling);

  // Coverage: every expected class should hold a fair share of the in-chord
  // energy. A missing third makes Am indistinguishable from A5 by purity alone.
  const classCount = target.pitchClasses.length;
  const fairShare = inSum / classCount;
  let coverageAcc = 0;
  if (fairShare > 0) {
    for (const pc of target.pitchClasses) {
      coverageAcc += clamp01(chroma[pc] / (fairShare * SCORING.coverageShare));
    }
  }
  const coverage = coverageAcc / classCount;

  const root = clamp01(chroma[target.root] / (maxChroma * SCORING.rootFullShare));

  return { purity, coverage, root, bass: bassTerm(chroma, target, maxChroma, bass) };
}

function bassTerm(
  chroma: Float32Array,
  target: ChordTarget,
  maxChroma: number,
  evidence?: BassEvidence,
): number {
  if (!evidence) {
    // No spectral evidence available (unit tests, or a frame before the first
    // spectrum). Fall back to the bass pitch class being present at all.
    if (!target.bass) return 0.5;
    return clamp01(chroma[target.bass.pitchClass] / (maxChroma * SCORING.rootFullShare));
  }

  const present = clamp01(
    (evidence.expectedDb - SCORING.bass.absentDb) / (SCORING.bass.presentDb - SCORING.bass.absentDb),
  );
  if (!Number.isFinite(evidence.unwantedDb)) return present;

  // A muted string that rings louder than the intended bass is the classic
  // beginner error (hitting the low E on a C chord). Penalise by how much it
  // dominates, not by its absolute level.
  const excess = evidence.unwantedDb - evidence.expectedDb;
  const penalty = clamp01((excess - SCORING.bass.toleratedExcessDb) / SCORING.bass.excessRangeDb);
  return clamp01(present * (1 - penalty));
}

/** Combine the terms with the configured weights into a single 0..1 score. */
export function combineTerms(terms: ScoreTerms): number {
  const w = SCORING.weights;
  return clamp01(
    terms.purity * w.purity + terms.coverage * w.coverage + terms.root * w.root + terms.bass * w.bass,
  );
}

/** Pitch class of a note, exported so the analyser need not import data/. */
export const pitchClassOf = midiToPitchClass;
